import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { projectName } from '@/i18n/data'
import { usePageLoader } from '@/components/LogoLoader'
import { api } from '@/api/client'
import { StatTile } from '@/components/StatTile'
import { SortableTable, type Column } from '@/components/SortableTable'
import { BarChart } from '@/charts/BarChart'
import { SERIES } from '@/charts/palette'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { dashboardMessages } from '@/i18n/messages/dashboard'

interface Kpi {
  key: string; label: string; value: number; unit: string
  priorValue: number | null; changePct: number | null
}
interface DayPoint { label: string; a: number; b: number }
interface NamedValue { name: string; value: number; secondary: number }
interface NamedCount { name: string; count: number; value: number }
interface TodayProject { name: string; questionnaires: number; crewDays: number; metres: number }
interface TodaySummary {
  date: string; questionnaires: number; crewDays: number; projectCount: number
  crewCount: number; metres: number; revenueSub: number; byProject: TodayProject[]
}
interface RtkMeasurement {
  segmentId: string; measuredAt: string; project: string; crew: string | null; type: string
  material: string | null; lengthM: number | null; points: number | null
  hasFix: boolean; city: string
}
interface OpsSection { kpis: Kpi[]; daily: DayPoint[]; topProjects: NamedValue[]; topCrews: NamedValue[] }
interface RtkSection { kpis: Kpi[]; daily: DayPoint[]; types: NamedCount[]; lastMeasured: string | null }
interface DashboardData {
  from: string; to: string; days: number; lastRefresh: string | null
  today: TodaySummary; latestRtk: RtkMeasurement[]; ops: OpsSection; rtk: RtkSection
}

/** ISO timestamp → full date. RTK stores a measurement date, not a real time,
 *  so we take the date part straight from the string (no timezone shift) and drop the clock. */
function fmtWhen(iso: string): string {
  return fmtDate(iso.slice(0, 10))
}

// KPI labels come from the API in English; translate the known keys, fall back to the API's label.
const KPI_KEYS = {
  metres: 'kpiMetres', revenue: 'kpiRevenue', crewdays: 'kpiCrewdays', crews: 'kpiCrews',
  projects: 'kpiProjects', hours: 'kpiHours', surveyed: 'kpiSurveyed', segments: 'kpiSegments',
  points: 'kpiPoints', ha: 'kpiHa', fix: 'kpiFix',
} as const

export function DashboardPage() {
  const t = useT(dashboardMessages)
  const kpiLabel = (k: Kpi) => {
    const key = KPI_KEYS[k.key as keyof typeof KPI_KEYS]
    return key ? t(key) : k.label
  }
  const { data, isPending, error } = useQuery<DashboardData>({
    queryKey: ['dashboard', 14],
    queryFn: () => api.get<DashboardData>('/api/dashboard?days=14'),
    // The dashboard is a live board — keep it fresh without a manual reload.
    refetchInterval: 60_000,
  })

  const loader = usePageLoader(isPending, t('loadingDashboard'))
  if (loader) return loader
  if (!data && !error) return null
  if (error) return <p className="text-sm text-status-critical">{t('couldNotLoad')}</p>

  const { today, latestRtk, ops, rtk } = data

  const todayCols: Column<TodayProject>[] = [
    { key: 'name', header: t('colProject'), sortValue: (r) => r.name.toLowerCase(),
      render: (r) => <span className="font-medium">{projectName(r.name)}</span> },
    { key: 'q', header: t('colQuestionnaires'), align: 'right', sortValue: (r) => r.questionnaires,
      render: (r) => fmtNumber(r.questionnaires) },
    { key: 'cd', header: t('colCrewDays'), align: 'right', sortValue: (r) => r.crewDays,
      render: (r) => fmtNumber(r.crewDays) },
    { key: 'm', header: t('colMetres'), align: 'right', sortValue: (r) => r.metres,
      render: (r) => `${fmtNumber(r.metres)} m` },
  ]

  const rtkCols: Column<RtkMeasurement>[] = [
    { key: 'when', header: t('colDate'), sortValue: (r) => r.measuredAt,
      render: (r) => <span className="whitespace-nowrap text-neutral-600 dark:text-neutral-300">{fmtWhen(r.measuredAt)}</span> },
    { key: 'project', header: t('colProject'), sortValue: (r) => r.project.toLowerCase(),
      render: (r) => <span className="font-medium">{projectName(r.project)}</span> },
    { key: 'crew', header: t('colCrew'), sortValue: (r) => (r.crew ?? '').toLowerCase(),
      render: (r) => r.crew ?? <span className="text-neutral-400">—</span> },
    { key: 'type', header: t('colType'), sortValue: (r) => r.type,
      render: (r) => <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{r.type}</span> },
    { key: 'len', header: t('colLength'), align: 'right', sortValue: (r) => r.lengthM ?? -1,
      render: (r) => (r.lengthM != null ? `${fmtNumber(r.lengthM, 1)} m` : <span className="text-neutral-400">—</span>) },
    { key: 'pts', header: t('colPoints'), align: 'right', sortValue: (r) => r.points ?? -1,
      render: (r) => (r.points != null ? fmtNumber(r.points) : <span className="text-neutral-400">—</span>) },
    { key: 'fix', header: t('colFix'), align: 'right', sortValue: (r) => (r.hasFix ? 1 : 0),
      render: (r) => (r.hasFix ? <span className="text-green-600">✓</span> : <span className="text-neutral-300 dark:text-neutral-600">—</span>) },
    { key: 'city', header: t('colCity'), sortValue: (r) => r.city.toLowerCase(),
      render: (r) => <span className="text-neutral-500">{r.city || '—'}</span> },
    { key: 'view', header: '', align: 'right',
      render: (r) => (
        <Link
          to={`/rtk?focus=${r.segmentId}`}
          title={t('viewOnMap')}
          aria-label={t('viewOnMap')}
          className="inline-flex items-center rounded-md px-2 py-1 text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </Link>
      ) },
  ]

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-neutral-500">
          {t('liveBoard')}
          {data.lastRefresh && t('readModelRefreshed', { date: fmtDate(data.lastRefresh) })}
        </p>
      </header>

      {/* ── TODAY ──────────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
            {t('today', { date: fmtDate(today.date) })}
          </h2>
          <span className="text-xs text-neutral-400">{t('latestDayHint')}</span>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatTile label={t('questionnaires')} value={today.questionnaires} unit="" />
          <StatTile label={t('crewDays')} value={today.crewDays} unit="" />
          <StatTile label={t('projects')} value={today.projectCount} unit="" />
          <StatTile label={t('activeCrews')} value={today.crewCount} unit="" />
          <StatTile label={t('executed')} value={today.metres} unit="m" />
          <StatTile label={t('revenueSub')} value={today.revenueSub} unit="EUR" />
        </div>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">{t('questionnairesByProject')}</h3>
          <SortableTable
            columns={todayCols}
            rows={today.byProject}
            rowKey={(r) => r.name}
            initialSort={{ key: 'q', dir: 'desc' }}
            empty={t('noQuestionnaires')}
          />
        </div>
      </section>

      {/* ── LATEST RTK MEASUREMENTS ────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{t('latestRtk')}</h2>
          <span className="text-xs text-neutral-400">
            {t('mostRecent', { count: latestRtk.length })}
            {rtk.lastMeasured && t('lastMeasured', { date: fmtDate(rtk.lastMeasured) })}
          </span>
        </div>
        <SortableTable
          columns={rtkCols}
          rows={latestRtk}
          rowKey={(r, i) => `${r.measuredAt}-${i}`}
          initialSort={{ key: 'when', dir: 'desc' }}
          empty={t('noRtk')}
        />
      </section>

      {/* ── LAST 14 DAYS (trend) ───────────────────────────────────────────── */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
          {t('lastDays', { count: data.days, from: fmtDate(data.from), to: fmtDate(data.to) })}
        </h2>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {ops.kpis.map((k) => (
            <StatTile key={k.key} label={kpiLabel(k)} value={k.value} unit={k.unit} changePct={k.changePct} />
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <BarChart
            title={t('chartExecutedByDay')} subtitle={t('chartExecutedByDaySub')}
            bars={ops.daily.map((p) => ({ label: p.label, value: p.a }))}
            unit="m" color={SERIES.primary}
          />
          <BarChart
            title={t('chartRtkByDay')} subtitle={t('chartRtkByDaySub')}
            bars={rtk.daily.map((p) => ({ label: p.label, value: p.b }))}
            unit="m" color={SERIES.secondary}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <BarChart
            title={t('chartProjectsByMetres')}
            bars={ops.topProjects.map((p) => ({ label: projectName(p.name), value: p.value }))}
            unit="m" color={SERIES.primary} orientation="bar"
          />
          <BarChart
            title={t('chartRtkByType')}
            subtitle={t('chartRtkByTypeSub')}
            bars={rtk.types.map((ty) => ({
              label: ty.name,
              value: ty.count,
              ...(ty.value > 0 ? { note: `${fmtNumber(Math.round(ty.value))} m` } : {}),
            }))}
            unit="" color={SERIES.secondary} orientation="bar"
          />
        </div>

        {/* RTK survey headline for the window, kept compact below the trend. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {rtk.kpis.map((k) => (
            <StatTile key={k.key} label={kpiLabel(k)} value={k.value} unit={k.unit} changePct={k.changePct} />
          ))}
        </div>
      </section>
    </div>
  )
}
